import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Wspólne komponenty aplikacji.
 *
 * Powstały, bo tabela była wcześniej napisana od zera pięć razy, a KPI trzy —
 * każde z drobnymi różnicami w odstępach. Jeden wzorzec na mechanizm oznacza,
 * że poprawka dostępności czy odstępu robi się raz.
 */

// ---------------------------------------------------------------- wskaźnik

export type Ton = 'neutral' | 'ok' | 'warn' | 'bad';

const TON_TEKST: Record<Ton, string> = {
  neutral: 'text-plum', ok: 'text-ok', warn: 'text-warn', bad: 'text-bad',
};
const TON_TLO: Record<Ton, string> = {
  neutral: 'bg-plum', ok: 'bg-ok', warn: 'bg-warn', bad: 'bg-bad',
};

export function Kpi({
  etykieta, wartosc, pod, ton = 'neutral', udzial, onClick, tytul,
}: {
  etykieta: string; wartosc: ReactNode; pod?: ReactNode; ton?: Ton;
  udzial?: number; onClick?: () => void; tytul?: string;
}) {
  const tresc = (
    <>
      <div className="text-label uppercase text-plum-muted mb-2">{etykieta}</div>
      <div className={`kpi ${TON_TEKST[ton]}`}>{wartosc}</div>
      {udzial !== undefined && (
        <div className="meter mt-3">
          <span className={TON_TLO[ton]} style={{ width: `${Math.min(100, Math.max(0, udzial * 100))}%` }} />
        </div>
      )}
      {pod && <div className="text-caption text-plum-muted mt-2">{pod}</div>}
    </>
  );
  // KPI, które prowadzi do danych, musi być przyciskiem — inaczej klawiatura go nie znajdzie.
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      title={tytul ?? 'Pokaż dane źródłowe'}
      className="text-left rounded-lg -m-2 p-2 transition-colors hover:bg-canvas"
    >
      {tresc}
      <span className="mt-2 inline-flex items-center gap-1 text-caption font-semibold text-violet">
        Pokaż dane <span aria-hidden>→</span>
      </span>
    </button>
  ) : (
    <div>{tresc}</div>
  );
}

// ---------------------------------------------------------------- stany

export function Pusto({ tytul, opis, akcja }: { tytul: string; opis?: string; akcja?: ReactNode }) {
  return (
    <div className="px-6 py-14 text-center">
      <p className="text-plum font-semibold">{tytul}</p>
      {opis && <p className="mt-1.5 text-body-sm text-plum-muted max-w-md mx-auto">{opis}</p>}
      {akcja && <div className="mt-4 flex justify-center">{akcja}</div>}
    </div>
  );
}

export function Blad({ tresc, ponow }: { tresc: string; ponow?: () => void }) {
  return (
    <div className="card p-5 border-bad/25">
      <p className="font-semibold text-bad">Nie udało się wczytać danych</p>
      <p className="mt-1.5 text-body-sm text-plum-soft">{tresc}</p>
      <p className="mt-1.5 text-caption text-plum-muted">
        Jeśli powtarza się przy każdej próbie, zgłoś to administratorowi wraz z godziną wystąpienia.
      </p>
      {ponow && <button className="btn-ghost mt-4" onClick={ponow}>Spróbuj ponownie</button>}
    </div>
  );
}

export function Ladowanie({ tekst = 'Wczytywanie…' }: { tekst?: string }) {
  return <p className="px-3 py-8 text-center text-body-sm text-plum-muted" role="status">{tekst}</p>;
}

// ---------------------------------------------------------------- tabela

export interface Kolumna<T> {
  klucz: string;
  naglowek: string;
  /** Wartość do sortowania; brak = kolumna nie sortuje. */
  sort?: (w: T) => string | number;
  /** Zawartość komórki. */
  render: (w: T) => ReactNode;
  prawo?: boolean;
  szer?: string;
}

export function Tabela<T>({
  dane, kolumny, klucz, onKlik, wybrany, pusto, laduje, podsumowanie,
}: {
  dane: T[];
  kolumny: Array<Kolumna<T>>;
  klucz: (w: T) => string | number;
  onKlik?: (w: T) => void;
  wybrany?: (w: T) => boolean;
  pusto?: ReactNode;
  laduje?: boolean;
  /** Wiersze wstawiane przed danymi, np. nagłówki grup. */
  podsumowanie?: ReactNode;
}) {
  const [sortKlucz, setSortKlucz] = useState<string | null>(null);
  const [malejaco, setMalejaco] = useState(false);

  const posortowane = useMemo(() => {
    const k = kolumny.find((c) => c.klucz === sortKlucz);
    if (!k?.sort) return dane;
    const f = k.sort;
    return [...dane].sort((a, b) => {
      const x = f(a), y = f(b);
      const w = typeof x === 'number' && typeof y === 'number'
        ? x - y
        : String(x).localeCompare(String(y), 'pl');
      return malejaco ? -w : w;
    });
  }, [dane, kolumny, sortKlucz, malejaco]);

  const przelacz = (k: Kolumna<T>) => {
    if (!k.sort) return;
    if (sortKlucz === k.klucz) setMalejaco((v) => !v);
    else { setSortKlucz(k.klucz); setMalejaco(false); }
  };

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto max-h-[70vh]">
        <table className="tbl">
          <thead>
            <tr>
              {kolumny.map((k) => (
                <th key={k.klucz} style={k.szer ? { width: k.szer } : undefined}
                    className={k.prawo ? 'text-right' : undefined}
                    aria-sort={sortKlucz === k.klucz ? (malejaco ? 'descending' : 'ascending') : undefined}>
                  {k.sort ? (
                    <button type="button" onClick={() => przelacz(k)}
                            className="inline-flex items-center gap-1 hover:text-plum">
                      {k.naglowek}
                      <span aria-hidden className={sortKlucz === k.klucz ? 'text-plum' : 'text-line-strong'}>
                        {sortKlucz === k.klucz ? (malejaco ? '▾' : '▴') : '▴'}
                      </span>
                    </button>
                  ) : k.naglowek}
                </th>
              ))}
            </tr>
          </thead>
          <tbody aria-live="polite">
            {podsumowanie}
            {laduje && (
              <tr><td colSpan={kolumny.length}><Ladowanie /></td></tr>
            )}
            {!laduje && posortowane.length === 0 && (
              <tr><td colSpan={kolumny.length}>{pusto ?? <Pusto tytul="Brak danych" />}</td></tr>
            )}
            {!laduje && posortowane.map((w) => {
              const klik = onKlik ? () => onKlik(w) : undefined;
              return (
                <tr
                  key={klucz(w)}
                  data-klik={klik ? '1' : undefined}
                  aria-selected={wybrany?.(w) || undefined}
                  // Klikalny wiersz musi być osiągalny z klawiatury — stąd tabIndex i Enter.
                  tabIndex={klik ? 0 : undefined}
                  onClick={klik}
                  onKeyDown={klik ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); klik(); } } : undefined}
                >
                  {kolumny.map((k) => (
                    <td key={k.klucz} className={k.prawo ? 'text-right' : undefined}>{k.render(w)}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- panel boczny

export function Panel({ tytul, pod, onZamknij, children }: {
  tytul: ReactNode; pod?: ReactNode; onZamknij: () => void; children: ReactNode;
}) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onZamknij(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onZamknij]);

  return (
    <>
      <div className="drawer-tlo" onClick={onZamknij} aria-hidden />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={typeof tytul === 'string' ? tytul : 'Szczegóły'}>
        <header className="sticky top-0 bg-white border-b border-line px-5 py-4 flex items-start gap-3">
          <div className="min-w-0">
            <h2 className="text-sub truncate">{tytul}</h2>
            {pod && <p className="text-caption text-plum-muted mt-0.5">{pod}</p>}
          </div>
          <button className="btn-quiet ml-auto shrink-0" onClick={onZamknij} aria-label="Zamknij panel">✕</button>
        </header>
        <div className="p-5">{children}</div>
      </aside>
    </>
  );
}

// ---------------------------------------------------------------- zakładki

export function Zakladki<T extends string>({ wartosc, opcje, onZmiana }: {
  wartosc: T; opcje: Array<[T, string]>; onZmiana: (w: T) => void;
}) {
  return (
    <div className="inline-flex gap-1 p-1 rounded-pill bg-canvas border border-line" role="tablist">
      {opcje.map(([k, tekst]) => (
        <button key={k} role="tab" aria-selected={wartosc === k}
                className={`subtab ${wartosc === k ? 'subtab-on' : ''}`}
                onClick={() => onZmiana(k)}>
          {tekst}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- odznaki

export function Odznaka({ ton = 'neutral', children }: { ton?: Ton | 'info'; children: ReactNode }) {
  const klasa = {
    neutral: 'badge-neutral', ok: 'badge-ok', warn: 'badge-warn', bad: 'badge-bad', info: 'badge-info',
  }[ton];
  return <span className={klasa}>{children}</span>;
}

/** Dane symulacyjne muszą być widoczne — inaczej ktoś weźmie je za produkcyjne. */
export function ZnacznikSymulacji() {
  return <Odznaka ton="warn">symulacja</Odznaka>;
}

// ---------------------------------------------------------------- zmiana wartości

/** Różnica względem poprzedniego okresu, w punktach procentowych albo wprost. */
export function Zmiana({ teraz, wczesniej, procent = false }: {
  teraz: number | null; wczesniej: number | null; procent?: boolean;
}) {
  if (teraz === null || wczesniej === null) return <span className="text-plum-muted">—</span>;
  const d = procent ? (teraz - wczesniej) * 100 : teraz - wczesniej;
  if (Math.abs(d) < (procent ? 0.5 : 0.01)) return <span className="text-plum-muted">bez zmian</span>;
  const dobrze = d > 0;
  return (
    <span className={dobrze ? 'text-ok' : 'text-bad'}>
      {dobrze ? '+' : ''}{procent ? `${d.toFixed(1)} p.p.` : d.toFixed(2)}
    </span>
  );
}

export { Fragment };
